import { PropsWithChildren, ReactNode, memo, useMemo, useRef, useState, MutableRefObject } from 'react';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';

import { ButtonV2, TextInput, Title, Select } from '@restworld/ui-ds';
import { Variable } from '@restworld/utility-types';
import { useIntersectionObserver } from '@restworld/utils-common';

import { LayoutWrapper } from './wrapper-layout';
import { EmployerFiltersModal } from '../components/employer-filters-modal';
import EmployerNoResults from '../components/employer-no-results';
import FallbackSpinner from '../components/fallback-spinner';
import { IEmployerFilterForm } from '../components/job-listing/employer-job-listing-filters';
import { EmployerPrimaryLayoutHeader } from './employer-primary-header';

type LayoutElementProps = {
  element?: ReactNode;
  options?: Variable[];
  title?: string;
  onChange?: (value?: string) => void;
  placeholder?: string;
  value?: string;
  filterCount?: number;
  content?: string;
  foundNoResults?: boolean;
  formRef?: MutableRefObject<IEmployerFilterForm>;
};

export interface EmployerPrimaryLayoutProps {
  pageTitle?: string;
  addButtonTitle?: string;
  totalSearchResults?: ReactNode;
  filterProps?: LayoutElementProps;
  searchProps?: LayoutElementProps;
  sortProps?: LayoutElementProps;
  noResultsFoundProps?: LayoutElementProps;
  title: string;
  onAddButtonClick?: () => void;
  loadMoreBtnProps?: {
    onClick: () => void;
    loading: boolean;
    hasNextPage: boolean;
  };
  additionalFilters?: ReactNode;
  loading?: boolean;
  onInfoIconClick?: () => void;
  isResultAndFilterElAlwaysOnSameLine?: boolean;
  renderCustomElement?: ReactNode;
}

export function EmployerPrimaryLayout({
  children,
  pageTitle,
  addButtonTitle,
  filterProps,
  searchProps,
  totalSearchResults,
  sortProps,
  noResultsFoundProps,
  title: headTitle,
  onAddButtonClick,
  loadMoreBtnProps,
  additionalFilters,
  loading,
  isResultAndFilterElAlwaysOnSameLine,
  onInfoIconClick,
  renderCustomElement,
}: PropsWithChildren<EmployerPrimaryLayoutProps>) {
  const { t: translateCommon } = useTranslation('common');
  const filtersContainerRef = useRef<HTMLDivElement>(null);
  const [isFilterContainerVisible, setIsFilterContainerVisible] = useState(false);

  useIntersectionObserver({
    targetRef: filtersContainerRef,
    options: { threshold: 0.1, rootMargin: '-220px 0px 0px 0px' },
    callback: ([entry]) => setIsFilterContainerVisible(entry.isIntersecting),
  });

  return (
    <LayoutWrapper title={translateCommon(headTitle)} isNavbarOffScreen={!isFilterContainerVisible}>
      <FallbackSpinner />
      <ContentWrapper>
        <ResponsiveContainer>
          <EmployerPrimaryLayoutHeader
            pageTitle={pageTitle}
            addButtonTitle={addButtonTitle}
            onAddButtonClick={onAddButtonClick}
            onInfoIconClick={onInfoIconClick}
            renderCustomElement={renderCustomElement}
          />
        </ResponsiveContainer>
        <EmployerPrimaryLayoutFilters
          searchProps={searchProps}
          filterProps={filterProps}
          sortProps={sortProps}
          totalSearchResults={totalSearchResults}
          additionalFilters={additionalFilters}
          isFilterContainerVisible={isFilterContainerVisible}
          filtersContainerRef={filtersContainerRef}
          loading={loading}
          isResultAndFilterElAlwaysOnSameLine={isResultAndFilterElAlwaysOnSameLine}
        />
        <ResponsiveContainer>
          <FlexContainer>
            <div tw="w-full">
              <EmployerPrimaryLayoutMainContent
                noResultsFoundProps={noResultsFoundProps}
                addButtonTitle={addButtonTitle}
                loadMoreBtnProps={loadMoreBtnProps}
                onAddButtonClick={onAddButtonClick}
              >
                {children}
              </EmployerPrimaryLayoutMainContent>
            </div>
          </FlexContainer>
        </ResponsiveContainer>
      </ContentWrapper>
    </LayoutWrapper>
  );
}

export default memo(EmployerPrimaryLayout);

export const EmployerPrimaryLayoutFilters = ({
  searchProps,
  filterProps,
  sortProps,
  totalSearchResults,
  additionalFilters,
  isFilterContainerVisible,
  filtersContainerRef,
  loading,
  isResultAndFilterElAlwaysOnSameLine,
}: Pick<
  EmployerPrimaryLayoutProps,
  | 'searchProps'
  | 'filterProps'
  | 'sortProps'
  | 'totalSearchResults'
  | 'additionalFilters'
  | 'loading'
  | 'isResultAndFilterElAlwaysOnSameLine'
> & {
  isFilterContainerVisible?: boolean;
  filtersContainerRef?: React.RefObject<HTMLDivElement>;
}) => {
  const { t: translateCommon } = useTranslation('common');

  const sortOptions = useMemo(
    () => sortProps?.options?.map((v) => ({ value: v.label, key: v.key, label: v.label })),
    [sortProps?.options]
  );

  return (
    <FiltersWrapper ref={filtersContainerRef} hasBottomBorder={!isFilterContainerVisible}>
      <ResponsiveContainer>
        <Searchbar>
          <TextInput
            id="search"
            icon={MagnifyingGlassIcon}
            bordercolor="gray"
            placeholder={searchProps?.placeholder ? translateCommon(searchProps.placeholder) : ''}
            inputStyle={tw`focus:(border border-purple-500 outline-none ring-0 shadow-[inset_0_0_0_1px_theme('colors.purple.500')])`}
            hasLeftPadding
            wrapperStyle={tw`w-full px-0`}
            onChange={(value) => searchProps?.onChange?.(value)}
            value={searchProps?.value}
            disabled={loading}
          />
        </Searchbar>
        <FiltersAndResultsRowWrapper>
          <ResultAndMainFiltersContainer isResultAndFilterElAlwaysOnSameLine={isResultAndFilterElAlwaysOnSameLine}>
            <ResultsContainer>
              <Title fontSize="xSmall" textColor="gray-600" twStyle={tw`capitalize`}>
                {translateCommon('results')}:
              </Title>
              <Title fontWeight="semibold" fontSize="small" twStyle={tw`lowercase`}>
                {totalSearchResults}
              </Title>
            </ResultsContainer>
            <FlexContainer>
              {sortProps && (
                <Select
                  options={sortOptions}
                  onChange={(value) => sortProps.onChange?.(value.key)}
                  placeholder={sortProps.placeholder}
                  value={sortOptions?.find((option) => option.key === sortProps.value)}
                  wrapperStyle={tw`w-[285px]`}
                  tertiaryLabel={`${translateCommon('order_by')}: `}
                />
              )}
              {filterProps && (
                <EmployerFiltersModal filterCount={filterProps?.filterCount} formRef={filterProps?.formRef}>
                  <div tw="w-full">{filterProps?.element}</div>
                </EmployerFiltersModal>
              )}
            </FlexContainer>
          </ResultAndMainFiltersContainer>
          {additionalFilters && <AdditionalFiltersWrapper>{additionalFilters}</AdditionalFiltersWrapper>}
        </FiltersAndResultsRowWrapper>
      </ResponsiveContainer>
    </FiltersWrapper>
  );
};

export const EmployerPrimaryLayoutMainContent = ({
  children,
  noResultsFoundProps,
  addButtonTitle,
  onAddButtonClick,
  loadMoreBtnProps,
}: PropsWithChildren<
  Pick<EmployerPrimaryLayoutProps, 'noResultsFoundProps' | 'addButtonTitle' | 'loadMoreBtnProps' | 'onAddButtonClick'>
>) => {
  const { t: translateCommon } = useTranslation('common');
  return (
    <MainContent>
      {noResultsFoundProps?.foundNoResults ? (
        <EmployerNoResults
          content={translateCommon(noResultsFoundProps.content)}
          title={translateCommon(noResultsFoundProps.title)}
          buttonEl={
            addButtonTitle ? (
              <ButtonV2 StartIcon={PlusIcon} onClick={onAddButtonClick}>
                {translateCommon(addButtonTitle)}
              </ButtonV2>
            ) : null
          }
        />
      ) : (
        <>
          {children}
          {loadMoreBtnProps.hasNextPage && (
            <div tw="flex justify-center pt-16">
              <ButtonV2
                StartIcon={PlusIcon}
                variant="tertiary"
                onClick={loadMoreBtnProps.onClick}
                loading={loadMoreBtnProps.loading}
              >
                {translateCommon('load_more_items')}
              </ButtonV2>
            </div>
          )}
        </>
      )}
    </MainContent>
  );
};

const Searchbar = styled.section`
  ${tw`flex justify-between gap-2 w-full`}
  ${tw`py-4 sm:(py-8) lg:(py-8)`}
`;

const ResultAndMainFiltersContainer = styled.section<{ isResultAndFilterElAlwaysOnSameLine?: boolean }>`
  ${tw`flex flex-col gap-3 justify-between w-full md:(flex-row gap-0)`}
  ${({ isResultAndFilterElAlwaysOnSameLine }) => (isResultAndFilterElAlwaysOnSameLine ? tw`flex-row` : tw``)}
`;

const MainContent = tw.main``;

const ResultsContainer = styled.div``;

const FlexContainer = styled.div`
  ${tw`flex gap-4 items-end`}
`;
const ContentWrapper = styled.div`
  ${tw`py-[63px] xl:py-[80px]`}
`;
const ResponsiveContainer = styled.div`
  ${tw`px-4 lg:px-10 xl:px-20 max-w-screen-xl mx-auto`};
`;
// ${tw`px-4 md:(mx-10 max-w-[1024px] min-w-[640px]) xl:(max-w-[1280px] min-w-[1024px] mx-20) 2xl:(max-w-[1280px] min-w-[1280px] mx-auto)`};
const FiltersWrapper = styled.div<{ hasBottomBorder?: boolean }>(({ hasBottomBorder }) => [
  tw`w-full pb-[3rem] md:(sticky top-0 bg-neutral-10 z-10)`,
  hasBottomBorder ? tw`md:(border-b border-neutral-40 pb-6)` : tw``,
]);
const AdditionalFiltersWrapper = styled.div`
  ${tw`flex flex-col gap-2 mt-4 min-w-fit md:(flex-row gap-4 mt-0)`}
`;
const FiltersAndResultsRowWrapper = styled.div`
  ${tw`flex flex-col justify-between md:(flex-row)`}
`;
export * from './employer-primary-header';
